import robotsParser from 'robots-parser';

/**
 * Robots.txt Validation Utility
 * Uses robots-parser for behavioral logic and a thin linter for syntax guards.
 */

const VALID_DIRECTIVES = [
    "user-agent",
    "allow",
    "disallow",
    "sitemap",
    "crawl-delay",
    "host"
];

export const validateRobotsTxt = (content, dealerDomain = "https://example.com") => {
    const errors = [];
    const warnings = [];
    
    // Normalize domain for the parser
    const baseUrl = dealerDomain.startsWith('http') ? dealerDomain : `https://${dealerDomain}`;
    const robotsUrl = `${baseUrl}/robots.txt`;

    if (!content || content.trim() === "") {
        return { errors, warnings };
    }


    // 1. Behavioral Validation using robots-parser
    // This handles wildcards, specificity, and rule prioritization correctly.
    const robots = robotsParser(robotsUrl, content);
    
    if (robots.isDisallowed(`${baseUrl}/`, '*')) {
        warnings.push({
            type: "full-block",
            message: 'Caution: You are using "Disallow: /" for all agents. This will block search engines from indexing your entire site.',
        });
    }

    // 2. Lightweight Syntax Linter for "High-Value Guards"
    // Standard parsers silently ignore malformed lines; we want to flag them for admins.
    const lines = content.split("\n");
    let hasUserAgent = false;

    lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        
        // Skip empty lines and comments
        if (trimmedLine === "" || trimmedLine.startsWith("#")) {
            return;
        }

        const match = trimmedLine.match(/^([\w-]+):\s*(.*)$/);
        const lineNumber = index + 1;

        if (!match) {
            errors.push({
                line: lineNumber,
                message: `Malformed line: "${trimmedLine}". Expected format: "Directive: Value"`,
            });
            return;
        }

        const directive = match[1].toLowerCase();
        const value = match[2].trim();

        if (!VALID_DIRECTIVES.includes(directive)) {
            errors.push({
                line: lineNumber,
                message: `Unknown directive: "${match[1]}". Valid directives are: User-agent, Allow, Disallow, Sitemap, Crawl-delay, Host.`,
            });
            return;
        }

        if (directive === "user-agent") {
            hasUserAgent = true;
        }

        // Check for rules appearing before any User-agent
        if (!hasUserAgent && (directive === "allow" || directive === "disallow" || directive === "crawl-delay")) {
            errors.push({
                line: lineNumber,
                message: `Directive "${match[1]}" must appear after a "User-agent" declaration.`,
            });
        }

        // Validate Sitemap URL format
        if (directive === "sitemap") {
            try {
                const url = new URL(value);
                if (url.protocol !== "http:" && url.protocol !== "https:") {
                    errors.push({
                        line: lineNumber,
                        message: "Sitemap URL must use http or https protocol.",
                    });
                }
            } catch (e) {
                errors.push({
                    line: lineNumber,
                    message: `Invalid Sitemap URL: "${value}". Must be a full absolute URL.`,
                });
            }
        }
    });

    return { errors, warnings };
};
